import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  useCourseState,
  useCourseDispatch,
  CourseActionType,
  ProvisionCourseStep,
} from "../../../context/CourseContext";
import { useDeliveryStrings } from "../useDeliveryStrings";
import { getStyles } from "../styles";
import AccessTable from "./AccessTable";
import courseService, { DeliveryRules } from "../../../services/CourseService";
import DeliverySchedule from "./DeliverySchedule";
import { PreviewScheduleResponse, ProvisionSiteCourseResponse } from "@beable/roster-backend-client-v1";
import BaseDialog from "../../Common/BaseDialog";
import CourseDashboardDialog from "./CourseDashboardDialog";
import { format } from "date-fns-tz";

export default function ApplyRules() {
  const theme = useTheme();
  const styles = getStyles(theme);
  const {
    customer,
    site,
    course,
    courseSet,
    schoolId,
    accessSelections,
    accessChanged,
    deliverySelections,
    deliveryChanged,
    currentAcademicYears,
    futureAcademicYears,
  } = useCourseState();
  const {
    getFrequencyString,
    getAvailabilityString,
    getQuantityString,
    getLanguageString,
    getIgnoreSkipDatesString
  } = useDeliveryStrings();
  const courseDispatch = useCourseDispatch();
  const [provisionCurrent, setProvisionCurrent] = useState<boolean>(false);
  const [forceProvision, setForceProvision] = useState(false);
  const [markUrgent, setMarkUrgent] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [previewSchedule, setPreviewSchedule] =
    useState<PreviewScheduleResponse>({} as PreviewScheduleResponse);
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(0);

  const getAcademicYears = useCallback(() => {
    let academicYears = [...futureAcademicYears];
    if (provisionCurrent) {
      academicYears = [...futureAcademicYears, ...currentAcademicYears];
    }

    return academicYears;
  }, [provisionCurrent, futureAcademicYears, currentAcademicYears]);

  useEffect(() => {
    if (!futureAcademicYears.length && currentAcademicYears.length) {
      setProvisionCurrent(true);
    }
  }, [futureAcademicYears, currentAcademicYears]);

  const provisionSiteCourse = useCallback(
    async (preview: boolean): Promise<PreviewScheduleResponse | ProvisionSiteCourseResponse | boolean> => {
      const academicYears = getAcademicYears();

      let data = false;
      //only call provisionSiteCourse if all neccassary data was set
      if (site && (course || courseSet) && schoolId && academicYears.length) {
        data = await courseService.provisionSiteCourse(
          site,
          course,
          courseSet,
          academicYears,
          schoolId !== "ALL" ? schoolId : undefined,
          accessChanged || forceProvision || preview
            ? JSON.stringify(accessSelections)
            : undefined,
          deliveryChanged || forceProvision || preview
            ? deliverySelections
            : undefined,
          preview,
          markUrgent
        );
      }

      return data;
    },
    [
      accessChanged,
      accessSelections,
      course,
      courseSet,
      deliveryChanged,
      deliverySelections,
      forceProvision,
      markUrgent,
      schoolId,
      site,
      getAcademicYears,
    ]
  );

  useEffect(() => {
    const fetchPreviewSchedule = async () => {
      const data = await provisionSiteCourse(true);
      if (data && typeof data === "object") {
        setPreviewSchedule(data as PreviewScheduleResponse);
      } else {
        setPreviewSchedule({} as PreviewScheduleResponse);
      }
    };

    fetchPreviewSchedule();
  }, [provisionSiteCourse]);

  const resetSite = () => {
    courseDispatch({
      type: CourseActionType.SiteChanged,
      site: site,
    });
    courseDispatch({
      type: CourseActionType.StepChanged,
      activeStep: ProvisionCourseStep.Access,
    });
  };

  const submit = async () => {
    const data = await provisionSiteCourse(false);

    if (data) {
      setEstimatedWaitTime((data as ProvisionSiteCourseResponse).estimatedWaitTime);
      setSuccessDialogOpen(true);
    } else {
      alert("There was an error submitting your request.");
    }
  };

  const closeSuccessDialog = () => {
    resetSite();
    setSuccessDialogOpen(false);
  };

  const openDashboardDialog = () => {
    setDashboardDialogOpen(true);
    setSuccessDialogOpen(false);
  };

  const closeProvisionStatusDialog = () => {
    resetSite();
    setDashboardDialogOpen(false);
  };

  const currentAcademicYearLabel = () => {
    return `Include current academic year (${format(new Date(currentAcademicYears[0].startAt), "yyyy-MM-dd")} - 
                ${format(new Date(currentAcademicYears[0].endAt), "yyyy-MM-dd")})`;
  }

  return (
    <Grid>
      <Typography variant="h6" sx={styles.stepContainer_header}>
        Provision for{" "}
        {course
          ? `course: ${course?.courseName}`
          : `course set: ${courseSet?.name}`}
      </Typography>
      <Grid container sx={styles.applyRules_section}>
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="h6" sx={styles.margin1}>
            Access Rules:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={accessChanged}
                name="accessChanged"
                disabled={true}
              />
            }
            label="Changes Made"
          />
        </Grid>
        <AccessTable />
      </Grid>
      <Box sx={styles.applyRules_section}>
        <Divider />
      </Box>
      <Grid container sx={styles.applyRules_section}>
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="h6" sx={styles.margin1}>
            Delivery Rules:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={deliveryChanged}
                name="deliveryChanged"
                disabled={true}
              />
            }
            label="Changes Made"
          />
        </Grid>
        {deliverySelections && (
          <Typography sx={styles.applyRules_sectionText}>
            - Availability: {getAvailabilityString(deliverySelections)} <br />-
            Site Closed Dates: { getIgnoreSkipDatesString(deliverySelections)} <br />-
            Frequency: {getFrequencyString(deliverySelections)} <br />- Lessons
            to Deliver: {getQuantityString(deliverySelections)} <br />-
            Language: {getLanguageString(deliverySelections)} <br />
            {futureAcademicYears[0] && (
              <span><br />- Academic Year: 
                {` ${format(new Date(futureAcademicYears[0].startAt), "yyyy-MM-dd")} - 
                ${format(new Date(futureAcademicYears[0].endAt), "yyyy-MM-dd")} `}<br />
              </span>
            )}

          </Typography>
        )}
      </Grid>
      <Box sx={styles.applyRules_section}>
        <Divider />
      </Box>
      <Grid container sx={styles.applyRules_section}>
        <Typography variant="h6" sx={styles.margin1}>
          Schedule:
        </Typography>
        {previewSchedule.schedule && previewSchedule.schedule.length > 0 ? (
          <DeliverySchedule previewSchedule={previewSchedule} />
        ) : (
          <Typography sx={styles.applyRules_sectionText}>
            No schedules will be created or updated.
          </Typography>
        )}
      </Grid>
      <Box sx={styles.applyRules_section}>
        <Divider />
      </Box>
      <Grid container sx={styles.applyRules_section}>
        {currentAcademicYears && currentAcademicYears.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={provisionCurrent}
                name="provisionCurrent"
                color="primary"
                onChange={() => setProvisionCurrent(!provisionCurrent)}
              />
            }
            label={currentAcademicYearLabel()}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={forceProvision}
              name="forceProvision"
              color="primary"
              onChange={() => setForceProvision(!forceProvision)}
              disabled={accessChanged || deliveryChanged}
            />
          }
          label="Force Provision (No changes made)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={markUrgent}
              name="markUrgent"
              color="primary"
              onChange={() => setMarkUrgent((prevState) => !prevState)}
            />
          }
          label="Mark as urgent"
        />

      </Grid>
      <Box sx={styles.applyRules_section}>
        <Divider />
      </Box>
      <Grid container sx={{
        justifyContent: "space-between"
      }}>
        <Button
          variant="contained"
          color="primary"
          sx={styles.button}
          aria-label="back"
          title="back"
          onClick={() =>
            courseDispatch({
              type: CourseActionType.StepChanged,
              activeStep: ProvisionCourseStep.Delivery,
            })
          }
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={styles.button}
          aria-label="save"
          title="save"
          onClick={submit}
        >
          Save
        </Button>
      </Grid>
      <BaseDialog
        open={successDialogOpen}
        header="Provision Status"
        showCloseIcon={true}
        showCloseButton={false}
        onClose={closeSuccessDialog}
      >
        <Box
          sx={{
            minHeight: 150,
            display: "flex",
            alignItems: "center"
          }}>
          <Box sx={{
            width: "100%"
          }}>
            <DialogContentText>
              Provision request submitted successfully!<br />Estimated time until processing: {estimatedWaitTime} minutes.
            </DialogContentText>
            <DialogActions>
              <Button
                onClick={openDashboardDialog}
                variant="contained"
                color="primary"
                title="dashboard"
              >
                View Dashboard
              </Button>

              <Button
                onClick={closeSuccessDialog}
                variant="contained"
                color="secondary"
                title="close"
              >
                Close
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </BaseDialog>
      <CourseDashboardDialog
        open={dashboardDialogOpen}
        customer={customer!}
        site={site!}
        academicYear={getAcademicYears().pop()!}
        course={course}
        courseSet={courseSet}
        handleClose={closeProvisionStatusDialog}
      />
    </Grid>
  );
}
